import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Sparkles from 'src/assets/svgs/Sparkles.svg'
import Accordion from 'src/components/Accordion/Accordion'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import Box from 'src/components/Box/Box'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import ButtonAndMoreDropdown from 'src/components/ButtonAndMoreDropdown/ButtonAndMoreDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ListBox, { TitledCell } from 'src/components/ListBox/ListBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { MoreDropdownDesigns } from 'src/components/MoreDropdown/MoreDropdown'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import SvgClockRewind from 'src/components/Svgs/SvgClockRewind'
import SvgPreviousArrow from 'src/components/Svgs/SvgPreviousArrow'
import SvgSettings from 'src/components/Svgs/SvgSettings'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import Trns from 'src/localization/Trns'
import {
  OrgExpenseReportCycle,
  OrgExpenseReportCycleModel,
} from 'src/models/orgExpenseReports.models'
import {
  orgExpenseReportsHistoricalReportsUrl,
  orgExpenseReportUrl,
  setOrgExpenseReportCycleUrl,
} from 'src/navigation/urls'
import {
  issuerGroupedRCNOrgExpenseReportCycles,
  useQueryOrgExpenseReportCycles,
} from 'src/queries/orgExpenseReports.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectAppName,
  selectBrandColorCta,
  selectBrandColorFontPrimary,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const ExpensesExpenseReports: React.FC = () => {
  const user = useSelector(selectUserListModelFromState)
  const appName = useSelector(selectAppName)
  const brandColorFontPrimary = useSelector(selectBrandColorFontPrimary)
  const brandColorCta = useSelector(selectBrandColorCta)
  const {
    show_automated_expense_review: showAutomatedExpenseReview
  } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const { data: reports, isLoading: loading } = useQueryOrgExpenseReportCycles({
    select: issuerGroupedRCNOrgExpenseReportCycles,
  })

  const { analytics } = useAnalyticsContext()

  const sortRcns = (
    a: OrgExpenseReportCycle,
    b: OrgExpenseReportCycle,
  ): number => {
    return a.accountName > b.accountName
      ? 1
      : b.accountName > a.accountName
        ? -1
        : 0
  }

  return (
    <>
      <Helmet>
        <title>{`${showAutomatedExpenseReview ? 'Statements' : 'Expense Reports'} | ${appName}`}</title>
        <meta
          name="description"
          content={`${showAutomatedExpenseReview ? 'Statements' : 'Expense Reports'} page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent noStackingIndex noPadding>
        {user?.hasOrgBookkeeperPermissions ? (
          loading ? (
            <LoadingBackgroundPage />
          ) : reports && reports.length <= 0 ? (
            <TableEmptyState
              design={TableEmptyStateDesigns.OrgExpenseReportCycleSettings}
            />
          ) : (
            <>
              {!showAutomatedExpenseReview && (
                <Box
                  noPadding
                  css={css`
                    margin: 0px 40px 10px;
                    display: flex;
                    flex-direction: row;
                    padding: 10px 16px;
                    align-items: center;
                    gap: 10px;
                `}
                >
                  <img
                    src={Sparkles}
                    alt={'sparkles'}
                    css={css`
                      filter: brightness(0) saturate(100%) invert(16%) sepia(97%)
                        saturate(4068%) hue-rotate(251deg) brightness(94%)
                        contrast(92%);
                      width: 24px;
                      height: 24px;
                    `}
                  />
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    <Trns
                      source={
                        'Expense Reports are automatically generated based on your selected billing cycle.'
                      }
                    />
                  </span>
                </Box>
              )}
              {reports &&
                reports.map((issuer) => {
                  return (
                    <Accordion
                      key={issuer.issuerName}
                      topContent={
                        <div
                          css={css`
                            display: flex;
                            flex-direction: row;
                            align-items: center;
                            justify-content: space-between;
                            width: 100%;
                          `}
                        >
                          <TitledCell
                            label={<Trns source={'Bank'} />}
                            content={
                              <span
                                css={css`
                                  ${getFontCss(
                                    20,
                                    themeConstants.colors.font_primary,
                                    styleConstants.fontFamilies.medium,
                                  )};
                                `}
                              >
                                {issuer.issuerName}
                              </span>
                            }
                            isTitle
                          />
                        </div>
                      }
                      collapsibleContent={
                        <>
                          {issuer.rcns
                            .sort(sortRcns)
                            .map((rcn: OrgExpenseReportCycleModel) => {
                              return (
                                <ListBox
                                  key={rcn.id}
                                  id={'rcn-' + rcn.id}
                                  linkProps={{
                                    to:
                                      rcn.currentReportId !== undefined
                                        ? orgExpenseReportsHistoricalReportsUrl(
                                            rcn.id,
                                          )
                                        : setOrgExpenseReportCycleUrl(rcn.id),
                                    ariaLabelSource: 'Account Expense Report',
                                  }}
                                  cells={[
                                    {
                                      isTitle: true,
                                      label: <Trns source={'Account Name'} />,
                                      content: (
                                        <div
                                          css={css`
                                            display: flex;
                                            flex-direction: row;
                                            gap: 8px;
                                          `}
                                        >
                                          <span
                                            id="title"
                                            css={css`
                                              ${getFontCss(
                                                16,
                                                themeConstants.colors
                                                  .font_primary,
                                                styleConstants.fontFamilies
                                                  .medium,
                                              )};
                                            `}
                                          >
                                            <span
                                              css={css`
                                                padding-inline-end: 8px;
                                              `}
                                            >
                                              {rcn.accountName}
                                            </span>
                                            <Tag
                                              text={rcn.accountLast4?.toString()}
                                              size={TagSizes.Medium}
                                            />
                                          </span>
                                          <SvgChevronRight
                                            id={'chevron'}
                                            css={css`
                                              transition: transform
                                                ${styleConstants.transitionSpeed
                                                  .fast}
                                                ease-out;
                                              transform: scale(calc(20 / 24));

                                              ${getBreakpointDown(
                                                breakpointSizes.SM,
                                              )(css`
                                                display: none;
                                              `)}
                                            `}
                                          />
                                        </div>
                                      ),
                                    },
                                    {
                                      label: <Trns source={'Account Holder'} />,
                                      content: (
                                        <>
                                          <UserAvatarAndName
                                            textCss={css`
                                              ${getFontCss(
                                                16,
                                                themeConstants.colors
                                                  .font_primary,
                                                styleConstants.fontFamilies
                                                  .regular,
                                              )};
                                            `}
                                            user={{
                                              id: rcn.accountUserId || '',
                                              fullNameOrEmail:
                                                rcn.accountUserFullName ||
                                                rcn.accountUserEmail ||
                                                '',
                                              initials:
                                                `${rcn.accountUserFirstName?.slice(0, 1)}${rcn.accountUserLastName?.slice(0, 1)}` ||
                                                rcn.accountUserEmail[0] ||
                                                '',
                                              avatarUrl:
                                                rcn.accountUserAvatarUrl,
                                              hasFullName: true,
                                            }}
                                          />
                                        </>
                                      ),
                                    },
                                    {
                                      label: (
                                        <Trns source={showAutomatedExpenseReview ? 'Statement End Date' : 'Report End Date'} />
                                      ),
                                      content:
                                        rcn.currentReportEndDate !==
                                        undefined ? (
                                          <>{rcn.cycleLabel}</>
                                        ) : (
                                          <HoverableLink
                                            to={setOrgExpenseReportCycleUrl(
                                              rcn.id,
                                            )}
                                            ariaLabel={'Add Account'}
                                            size={HoverableLinkSizes.Large}
                                            underline
                                          >
                                            <Trns source={showAutomatedExpenseReview ? 'Set up statement' : 'Set up report'} />
                                          </HoverableLink>
                                        ),
                                    },
                                  ]}
                                  icon={
                                    rcn.currentReportId !== undefined ? (
                                      <ButtonAndMoreDropdown
                                        buttonProps={{
                                          design: ButtonDesigns.plainNoShadow,
                                          type: ButtonTypes.button,
                                          text: (
                                            <Trns
                                              source={showAutomatedExpenseReview ? 'View Current Statement' : 'View Current Report'}
                                            />
                                          ),
                                          id: 'viewCurrentReport',
                                          testId: 'viewCurrentReport',
                                          linkProps: {
                                            to: orgExpenseReportUrl(
                                              rcn.id,
                                              rcn.currentReportId,
                                            ),
                                            ariaLabel: 'View Current Report',
                                          },
                                          onClick: (): void => {
                                            analytics?.track(
                                              'expense reports view report',
                                              {
                                                report_id: rcn.currentReportId,
                                              },
                                            )
                                          },
                                        }}
                                        moreDropdownProps={{
                                          id: 'moreDropdown-' + rcn.id,
                                          design:
                                            MoreDropdownDesigns.WhiteToGray,
                                          minWidth: 270,
                                          verticalBuffer: 10,
                                          topOptions: [
                                            {
                                              id: 'viewPreviousReport',
                                              testId: 'viewPreviousReport',
                                              key: 'viewPreviousReport',
                                              label: (
                                                <div
                                                  css={css`
                                                    display: flex;
                                                    flex-direction: row;
                                                    align-items: center;
                                                    gap: 8px;
                                                    left: -2px;
                                                    position: relative;
                                                    ${getFontCss(
                                                      14,
                                                      themeConstants.colors.cta,
                                                      styleConstants
                                                        .fontFamilies.regular,
                                                    )};
                                                  `}
                                                >
                                                  <SvgPreviousArrow />
                                                  <Trns
                                                    source={
                                                      showAutomatedExpenseReview ? 'View Previous Statement' : 'View Previous Report'
                                                    }
                                                  />
                                                </div>
                                              ),
                                              grayed:
                                                rcn.previousReportId ===
                                                undefined,
                                              to:
                                                rcn.previousReportId &&
                                                orgExpenseReportUrl(
                                                  rcn.id,
                                                  rcn.previousReportId,
                                                ),
                                              ariaLabelSource:
                                                showAutomatedExpenseReview ? 'View Previous Statement' : 'View Previous Report',
                                              onClick: (): void => {
                                                analytics?.track(
                                                  'expense reports overflow menu',
                                                  {
                                                    menu_selection:
                                                      'previous report',
                                                  },
                                                )
                                              },
                                            },
                                            {
                                              id: 'historicalReports',
                                              testId: 'historicalReports',
                                              key: 'historicalReports',
                                              label: (
                                                <div
                                                  css={css`
                                                    display: flex;
                                                    flex-direction: row;
                                                    align-items: center;
                                                    gap: 8px;
                                                    ${getFontCss(
                                                      14,
                                                      themeConstants.colors.cta,
                                                      styleConstants
                                                        .fontFamilies.regular,
                                                    )};
                                                  `}
                                                >
                                                  <SvgClockRewind />
                                                  <Trns
                                                    source={showAutomatedExpenseReview ? 'View All Statements' : 'View All Reports'}
                                                  />
                                                </div>
                                              ),
                                              to: orgExpenseReportsHistoricalReportsUrl(
                                                rcn.id,
                                              ),
                                              ariaLabelSource:
                                                'View All Reports',
                                              onClick: (): void => {
                                                analytics?.track(
                                                  'expense reports overflow menu',
                                                  {
                                                    menu_selection:
                                                      'all reports',
                                                  },
                                                )
                                              },
                                            },
                                          ],
                                          bottomOptions: [
                                            {
                                              id: 'cycleSettings',
                                              testId: 'cycleSettings',
                                              key: 'cycleSettings',
                                              label: (
                                                <div
                                                  css={css`
                                                    display: flex;
                                                    flex-direction: row;
                                                    align-items: center;
                                                    gap: 8px;
                                                    left: -6px;
                                                    position: relative;
                                                    ${getFontCss(
                                                      14,
                                                      themeConstants.colors.cta,
                                                      styleConstants
                                                        .fontFamilies.regular,
                                                    )};
                                                  `}
                                                >
                                                  <SvgSettings />
                                                  <Trns
                                                    source={
                                                      showAutomatedExpenseReview ? 'Go to Statement Cycle Settings' : 'Go to Report Cycle Settings'
                                                    }
                                                  />
                                                </div>
                                              ),
                                              to: setOrgExpenseReportCycleUrl(
                                                rcn.id,
                                              ),
                                              ariaLabelSource:
                                                showAutomatedExpenseReview ? 'Go to Statement Cycle Settings' : 'Go to Report Cycle Settings',
                                              onClick: (): void => {
                                                analytics?.track(
                                                  'expense reports overflow menu',
                                                  {
                                                    menu_selection:
                                                      'report cycle',
                                                  },
                                                )
                                              },
                                            },
                                          ],
                                          hover: true,
                                          bottomOptionsCtaStyle: true,
                                          openComponent: (
                                            <SvgChevronUp
                                              css={css`
                                                transform: scale(0.667);
                                              `}
                                              fill={brandColorCta}
                                            />
                                          ),
                                          closedComponent: (
                                            <SvgChevronDown
                                              css={css`
                                                transform: scale(0.667);
                                              `}
                                              fill={brandColorFontPrimary}
                                            />
                                          ),
                                        }}
                                      />
                                    ) : (
                                      <div
                                        css={css`
                                          width: 220px;
                                        `}
                                      ></div>
                                    )
                                  }
                                />
                              )
                            })}
                        </>
                      }
                    />
                  )
                })}
            </>
          )
        ) : (
          <NotFoundBackgroundPageContent />
        )}
      </BackgroundPageContent>
    </>
  )
}

export default ExpensesExpenseReports
